import { useState, useEffect } from "react";
import { useCart } from "@/contexts/CartContext";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/logger";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShoppingCart, Minus, Plus, Trash2, Loader2, ArrowLeft, Truck } from "lucide-react";

const ALLOWED_COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "NL", name: "Netherlands" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "DK", name: "Denmark" },
  { code: "FI", name: "Finland" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "PT", name: "Portugal" },
  { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" },
  { code: "CH", name: "Switzerland" },
  { code: "IE", name: "Ireland" },
  { code: "NZ", name: "New Zealand" },
  { code: "JP", name: "Japan" },
];

interface ShippingOption {
  id: string;
  name: string;
  cost: number;
  currency: string;
}

interface ShippingAddress {
  country: string;
  first_name: string;
  last_name: string;
  address1: string;
  city: string;
  region: string;
  zip: string;
}

const emptyAddress: ShippingAddress = {
  country: "",
  first_name: "",
  last_name: "",
  address1: "",
  city: "",
  region: "",
  zip: "",
};

type CartStep = "cart" | "shipping" | "rates";

const CartDrawer = () => {
  const { items, removeFromCart, updateQuantity, cartCount, cartTotal, setOpenCartFn } = useCart();
  const [open, setOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [step, setStep] = useState<CartStep>("cart");
  const [address, setAddress] = useState<ShippingAddress>(emptyAddress);
  const [customerEmail, setCustomerEmail] = useState("");
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  const [selectedShipping, setSelectedShipping] = useState<ShippingOption | null>(null);
  const [isFetchingRates, setIsFetchingRates] = useState(false);
  const [shippingError, setShippingError] = useState<string | null>(null);

  useEffect(() => {
    setOpenCartFn(() => setOpen(true));
  }, [setOpenCartFn]);

  const resetShipping = () => {
    setStep("cart");
    setAddress(emptyAddress);
    setCustomerEmail("");
    setShippingOptions([]);
    setSelectedShipping(null);
    setShippingError(null);
  };

  const handleOpenChange = (v: boolean) => {
    setOpen(v);
    if (!v) resetShipping();
  };

  const handleCalculateShipping = async () => {
    if (!address.country || !address.city || !address.zip) {
      setShippingError("Please fill in country, city, and zip code.");
      return;
    }
    if (!address.first_name.trim() || !address.last_name.trim() || !customerEmail.trim() || !customerEmail.includes("@")) {
      setShippingError("Please provide your name and a valid email address.");
      return;
    }
    setIsFetchingRates(true);
    setShippingError(null);
    try {
      const shopId = items[0]?.shopId || "";
      const { data, error } = await supabase.functions.invoke("calculate-shipping", {
        body: {
          address,
          items: items.map((i) => ({
            product_id: i.productId,
            variant_id: i.printifyVariantId,
            quantity: i.quantity,
          })),
          shop_id: shopId,
        },
      });
      if (error) throw error;
      const options: ShippingOption[] = data?.shipping_options ?? [];
      if (options.length === 0) {
        setShippingError("No shipping options available for this address.");
        return;
      }
      setShippingOptions(options);
      setSelectedShipping(options[0]);
      setStep("rates");
    } catch (err: any) {
      logError("Shipping calculation error:", err);
      setShippingError("Unable to calculate shipping. Please check your address and try again.");
    } finally {
      setIsFetchingRates(false);
    }
  };

  const handleCheckout = async () => {
    if (items.length === 0 || !selectedShipping) return;
    setIsCheckingOut(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-merch-checkout", {
        body: {
          items: items.map((i) => ({
            productTitle: i.productTitle,
            productId: i.productId,
            variantTitle: i.variantTitle,
            printifyVariantId: i.printifyVariantId,
            shopId: i.shopId,
            price: i.price,
            quantity: i.quantity,
            imageUrl: i.imageUrl,
          })),
          shippingCost: selectedShipping.cost,
          shippingName: selectedShipping.name,
          customerName: `${address.first_name.trim()} ${address.last_name.trim()}`,
          customerEmail: customerEmail.trim(),
          shippingAddress: address,
        },
      });
      if (error) throw error;
      if (data?.url) {
        // Use location.href on mobile to avoid popup blockers
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          window.location.href = data.url;
        } else {
          window.open(data.url, "_blank");
        }
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      logError("Checkout error:", err);
    } finally {
      setIsCheckingOut(false);
    }
  };

  const grandTotal = cartTotal + (selectedShipping?.cost ?? 0);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <button className="relative rounded-md p-2 text-muted-foreground transition-colors hover:text-primary" aria-label="Open cart">
          <ShoppingCart className="h-5 w-5" />
          {cartCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {cartCount > 99 ? "99+" : cartCount}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="flex flex-col">
        <SheetHeader>
          <SheetTitle>
            {step === "cart" && `Your Cart (${cartCount})`}
            {step === "shipping" && "Shipping Address"}
            {step === "rates" && "Shipping Options"}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-muted-foreground">Your cart is empty</p>
          </div>
        ) : (
          <>
            {/* STEP: Cart Items */}
            {step === "cart" && (
              <>
                <div className="flex-1 overflow-y-auto space-y-4 py-4">
                  {items.map((item) => (
                    <div key={`${item.productId}-${item.printifyVariantId}`} className="flex gap-3 rounded-lg border border-border p-3">
                      {item.imageUrl && (
                        <img src={item.imageUrl} alt={item.productTitle} className="h-16 w-16 flex-shrink-0 rounded-md object-cover" />
                      )}
                      <div className="flex flex-1 flex-col gap-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{item.productTitle}</p>
                        {item.variantTitle && (
                          <p className="text-xs text-muted-foreground truncate">{item.variantTitle}</p>
                        )}
                        <p className="text-sm font-semibold text-primary">${item.price.toFixed(2)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <button
                            onClick={() => updateQuantity(item.productId, item.printifyVariantId, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-50"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="text-sm font-medium text-foreground w-6 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.productId, item.printifyVariantId, item.quantity + 1)}
                            className="rounded p-1 text-muted-foreground hover:text-foreground"
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => removeFromCart(item.productId, item.printifyVariantId)}
                            className="ml-auto rounded p-1 text-muted-foreground hover:text-destructive"
                            aria-label="Remove item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-border pt-4 space-y-3">
                  <div className="flex justify-between text-base font-semibold text-foreground">
                    <span>Subtotal</span>
                    <span>${cartTotal.toFixed(2)}</span>
                  </div>
                  <Button className="w-full gap-2" size="lg" onClick={() => setStep("shipping")}>
                    <Truck className="h-5 w-5" /> Calculate Shipping
                  </Button>
                </div>
              </>
            )}

            {/* STEP: Shipping Address */}
            {step === "shipping" && (
              <>
                <div className="flex-1 overflow-y-auto space-y-4 py-4">
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="ship-country" className="text-sm font-medium text-foreground">Country</Label>
                      <Select value={address.country} onValueChange={(v) => setAddress((a) => ({ ...a, country: v }))}>
                        <SelectTrigger id="ship-country" className="mt-1">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {ALLOWED_COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="ship-fname" className="text-sm font-medium text-foreground">First Name</Label>
                        <Input id="ship-fname" className="mt-1" value={address.first_name} onChange={(e) => setAddress((a) => ({ ...a, first_name: e.target.value }))} />
                      </div>
                      <div>
                        <Label htmlFor="ship-lname" className="text-sm font-medium text-foreground">Last Name</Label>
                        <Input id="ship-lname" className="mt-1" value={address.last_name} onChange={(e) => setAddress((a) => ({ ...a, last_name: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="cust-email" className="text-sm font-medium text-foreground">Email</Label>
                      <Input id="cust-email" type="email" className="mt-1" placeholder="jane@example.com" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="ship-addr" className="text-sm font-medium text-foreground">Address</Label>
                      <Input id="ship-addr" className="mt-1" value={address.address1} onChange={(e) => setAddress((a) => ({ ...a, address1: e.target.value }))} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="ship-city" className="text-sm font-medium text-foreground">City</Label>
                        <Input id="ship-city" className="mt-1" value={address.city} onChange={(e) => setAddress((a) => ({ ...a, city: e.target.value }))} />
                      </div>
                      <div>
                        <Label htmlFor="ship-region" className="text-sm font-medium text-foreground">State / Region</Label>
                        <Input id="ship-region" className="mt-1" value={address.region} onChange={(e) => setAddress((a) => ({ ...a, region: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="ship-zip" className="text-sm font-medium text-foreground">Zip / Postal Code</Label>
                      <Input id="ship-zip" className="mt-1" value={address.zip} onChange={(e) => setAddress((a) => ({ ...a, zip: e.target.value }))} />
                    </div>
                  </div>
                  {shippingError && (
                    <p className="text-sm text-destructive">{shippingError}</p>
                  )}
                </div>
                <div className="border-t border-border pt-4 space-y-3">
                  <Button className="w-full gap-2" size="lg" onClick={handleCalculateShipping} disabled={isFetchingRates}>
                    {isFetchingRates ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> Calculating…</>
                    ) : (
                      "Get Shipping Rates"
                    )}
                  </Button>
                  <Button variant="ghost" className="w-full gap-2" onClick={() => { setStep("cart"); setShippingError(null); }}>
                    <ArrowLeft className="h-4 w-4" /> Back to Cart
                  </Button>
                </div>
              </>
            )}

            {/* STEP: Rate Selection */}
            {step === "rates" && (
              <>
                <div className="flex-1 overflow-y-auto space-y-4 py-4">
                  <div className="space-y-2">
                    {shippingOptions.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setSelectedShipping(opt)}
                        className={`w-full flex items-center justify-between rounded-lg border p-3 text-left transition-colors ${
                          selectedShipping?.id === opt.id
                            ? "border-primary bg-primary/5 ring-2 ring-primary"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <span className="text-sm font-medium text-foreground">{opt.name}</span>
                        <span className="text-sm font-semibold text-primary">${opt.cost.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="border-t border-border pt-4 space-y-3">
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Subtotal</span>
                      <span>${cartTotal.toFixed(2)}</span>
                    </div>
                    {selectedShipping && (
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>Shipping</span>
                        <span>${selectedShipping.cost.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-semibold text-foreground pt-1">
                      <span>Total</span>
                      <span>${grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                  <Button className="w-full gap-2" size="lg" onClick={handleCheckout} disabled={isCheckingOut || !selectedShipping}>
                    {isCheckingOut ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> Processing…</>
                    ) : (
                      "Checkout"
                    )}
                  </Button>
                  <Button variant="ghost" className="w-full gap-2" onClick={() => setStep("shipping")}>
                    <ArrowLeft className="h-4 w-4" /> Change Address
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
